import React, { useRef, useEffect, useState, useCallback } from 'react';
import flvjs from 'flv.js';
import { useDispatch } from 'react-redux';
import { MdFullscreen, MdPlayArrow, MdStop, MdVolumeOff, MdVolumeUp, MdRefresh } from 'react-icons/md';
import { updateChannelState } from '../../../redux/reducers/dashcamSlice';

const StatusPill = ({ playerState, errorMessage }) => {
  if (playerState === 'loading') {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-amber-200/30 bg-amber-400/15 px-3 py-1 text-[11px] font-medium text-amber-100 backdrop-blur-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" />
        Connexion...
      </div>
    );
  }

  if (playerState === 'playing') {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/30 bg-emerald-400/15 px-3 py-1 text-[11px] font-medium text-emerald-100 backdrop-blur-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
        En service
      </div>
    );
  }

  if (playerState === 'error') {
    return (
      <div className="inline-flex max-w-[240px] items-center gap-2 rounded-full border border-rose-200/30 bg-rose-400/15 px-3 py-1 text-[11px] font-medium text-rose-100 backdrop-blur-sm">
        <span className="h-2 w-2 rounded-full bg-rose-300" />
        <span className="truncate">{errorMessage || 'Erreur de flux'}</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200/20 bg-slate-400/10 px-3 py-1 text-[11px] font-medium text-slate-100 backdrop-blur-sm">
      <span className="h-2 w-2 rounded-full bg-slate-300" />
      Arrete
    </div>
  );
};

const FlvPlayer = ({ channel, width = '100%', height = '100%', isEnabled = true }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const timeoutRef = useRef(null);
  const initTokenRef = useRef(0);
  const playerStateRef = useRef('idle');
  const dispatch = useDispatch();

  const [playerState, setPlayerState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);
  const [isMuted, setIsMuted] = useState(channel.isMuted ?? true);

  const setPlayerStatus = useCallback((nextState, nextError = null) => {
    playerStateRef.current = nextState;
    setPlayerState(nextState);
    setErrorMessage(nextError);
    dispatch(updateChannelState({
      channelId: channel.id,
      playerState: nextState,
      errorMessage: nextError,
    }));
  }, [channel.id, dispatch]);

  const isInterruptedPlayError = useCallback((error) => {
    const message = String(error?.message || error || '').toLowerCase();
    return (
      message.includes('interrupted by a new load request') ||
      message.includes('the play() request was interrupted') ||
      message.includes('aborterror')
    );
  }, []);

  const destroyPlayer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    initTokenRef.current += 1;

    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload();
        playerRef.current.detachMediaElement();
        playerRef.current.destroy();
      } catch (error) {
        console.warn('Error destroying player:', error);
      }
      playerRef.current = null;
    }
  }, []);

  const initPlayer = useCallback(() => {
    if (!flvjs.isSupported() || !videoRef.current || !isEnabled || !channel.url) {
      setPlayerStatus('error', 'Flux direct non supporte sur ce navigateur');
      return;
    }

    try {
      destroyPlayer();
      const initToken = initTokenRef.current;
      setPlayerStatus('loading', null);

      videoRef.current.muted = isMuted;

      const player = flvjs.createPlayer({
        type: 'flv',
        url: channel.url,
        isLive: true,
        enableWorker: true,
        enableStashBuffer: true,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 5,
        autoCleanupMinBackwardDuration: 1,
        lazyLoad: false,
        hasAudio: false,
        hasVideo: true,
      });

      playerRef.current = player;
      player.attachMediaElement(videoRef.current);
      player.load();

      const handlePlaying = () => {
        if (initToken !== initTokenRef.current) return;
        setPlayerStatus('playing', null);
      };

      const handleVideoError = (event) => {
        if (initToken !== initTokenRef.current) return;
        const nextMessage = event?.message || event?.target?.error?.message || 'Flux interrompu';
        setPlayerStatus('error', nextMessage);
      };

      videoRef.current.addEventListener('playing', handlePlaying);
      videoRef.current.addEventListener('error', handleVideoError);

      timeoutRef.current = setTimeout(() => {
        if (initToken === initTokenRef.current && playerStateRef.current === 'loading') {
          setPlayerStatus('error', 'Delai de connexion depasse');
        }
      }, 30000);

      Promise.resolve(player.play()).catch((error) => {
        if (initToken !== initTokenRef.current) return;
        if (isInterruptedPlayError(error)) {
          console.warn('play() interrompu par une reinitialisation du flux:', error);
          return;
        }
        setPlayerStatus('error', `Lecture impossible: ${error.message}`);
      });
    } catch (error) {
      setPlayerStatus('error', `Erreur: ${error.message}`);
    }
  }, [channel.url, destroyPlayer, isEnabled, isInterruptedPlayError, isMuted, setPlayerStatus]);

  const stopPlayer = useCallback(() => {
    destroyPlayer();
    setPlayerStatus('stopped', null);
  }, [destroyPlayer, setPlayerStatus]);

  const togglePlayStop = useCallback(() => {
    if (playerState === 'playing' || playerState === 'loading') {
      stopPlayer();
      return;
    }
    initPlayer();
  }, [initPlayer, playerState, stopPlayer]);

  const handleRetry = useCallback(() => {
    initPlayer();
  }, [initPlayer]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const nextMuted = !videoRef.current.muted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
  }, []);

  const requestFullscreen = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    setIsMuted(channel.isMuted ?? true);
  }, [channel.isMuted]);

  useEffect(() => {
    if (isEnabled && channel.isPlaying) {
      initPlayer();
    } else {
      stopPlayer();
    }

    return () => {
      destroyPlayer();
    };
  }, [channel.isPlaying, destroyPlayer, initPlayer, isEnabled, stopPlayer]);

  return (
    <div
      className="relative h-full min-h-[260px] overflow-hidden rounded-xl border border-[#cfe0dc] bg-[#08141a] shadow-[0_18px_40px_rgba(8,20,26,0.18)]"
      style={{ width, height }}
    >
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        autoPlay
        muted={isMuted}
        playsInline
      />

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#08141a]/10 via-transparent to-[#08141a]/70" />

      <div className="absolute left-3 top-3 z-20 flex items-center gap-2">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0d2a34]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-md backdrop-blur-sm">
          <span className={`h-2 w-2 rounded-full ${playerState === 'playing' ? 'bg-[#34d399]' : playerState === 'loading' ? 'bg-[#fbbf24]' : 'bg-[#f87171]'}`} />
          {playerState === 'playing' ? 'Flux Direct' : playerState === 'loading' ? 'Connexion' : 'Incident'}
        </div>
      </div>

      <div className="absolute right-3 top-3 z-20">
        <StatusPill playerState={playerState} errorMessage={errorMessage} />
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-white/10 bg-[#08141a]/90 px-4 py-3 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={togglePlayStop}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/8 text-white transition hover:bg-white/16"
              title={playerState === 'playing' || playerState === 'loading' ? 'Arreter le flux' : 'Lancer le flux'}
            >
              {playerState === 'playing' || playerState === 'loading' ? <MdStop className="text-[18px]" /> : <MdPlayArrow className="text-[20px]" />}
            </button>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/8 text-white transition hover:bg-white/16"
              title="Recharger le flux"
            >
              <MdRefresh className="text-[18px]" />
            </button>
            <button
              type="button"
              onClick={toggleMute}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/8 text-white transition hover:bg-white/16"
              title={isMuted ? 'Activer le son' : 'Couper le son'}
            >
              {isMuted ? <MdVolumeOff className="text-[18px]" /> : <MdVolumeUp className="text-[18px]" />}
            </button>
          </div>

          <div className="min-w-0 flex-1 px-2 text-center">
            <div className="truncate text-sm font-semibold text-white">
              {channel.id?.replace(/^[^-]+-/, '') || 'Camera en direct'}
            </div>
            <div className="text-[11px] text-[#b8c7c4]">
              {playerState === 'playing' ? 'Surveillance en temps reel' : playerState === 'loading' ? 'Initialisation du flux securise' : 'Verification requise'}
            </div>
          </div>

          <button
            type="button"
            onClick={requestFullscreen}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/8 text-white transition hover:bg-white/16"
            title="Plein ecran"
          >
            <MdFullscreen className="text-[18px]" />
          </button>
        </div>
      </div>

      {playerState === 'error' && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#08141a]/78 px-6">
          <div className="w-full max-w-sm rounded-2xl border border-[#fca5a5]/30 bg-[#10222b] p-5 text-center shadow-xl">
            <div className="text-sm font-semibold text-white">Flux direct indisponible</div>
            <p className="mt-2 text-xs leading-5 text-[#d6e0dd]">{errorMessage}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#0f766e] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#115e59]"
            >
              Relancer le flux
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlvPlayer;
